import { Point } from "../../engine/space/bounds";
import {
  allDirections,
  genAllDirections,
  getInDirection,
} from "../../engine/space/directions";

const KEY_N = 10000;

export class DMap {
  map: { [key: number]: number };

  constructor(map: { [key: number]: number }) {
    this.map = map;
  }

  get(pos: Point) {
    return this.map[pos.x * KEY_N + pos.y];
  }

  getLowestAdjacent(
    pos: Point,
    openness: (pos: Point, from: Point) => boolean
  ) {
    let lowest: Point[] = [],
      nextValue;
    let lowValue = Number.MAX_SAFE_INTEGER;
    for (const next of genAllDirections(pos)) {
      nextValue = this.get(next);
      if (nextValue < lowValue && openness(next, pos)) {
        lowValue = nextValue;
        lowest = [next];
      } else if (nextValue === lowValue) {
        lowest.push(next);
      }
    }
    return lowest;
  }

  getHighestAdjacent(
    pos: Point,
    openness: (pos: Point, from: Point) => boolean
  ) {
    let highest: Point[] = [],
      nextValue;
    let highValue = Number.MAX_SAFE_INTEGER;
    for (const next of genAllDirections(pos)) {
      nextValue = this.get(next);
      if (highValue < nextValue && openness(next, pos)) {
        highValue = nextValue;
        highest = [next];
      } else if (nextValue === highValue) {
        highest.push(next);
      }
    }
    return highest;
  }

  getSortedAdjacent(
    pos: Point,
    openness: (pos: Point, from: Point) => boolean,
    onlyEqualOrLower?: boolean
  ) {
    const result: Point[] = [];
    for (const next of genAllDirections(pos)) {
      if (openness(next, pos)) {
        result.push(next);
      }
    }
    const sortedResult = result.sort((a, b) => {
      const av = this.get(a);
      const bv = this.get(b);
      return av == null ? 1 : bv == null ? -1 : av - bv;
    });

    if (onlyEqualOrLower) {
      const starting = this.get(pos);
      return sortedResult.filter((p) => this.get(p) <= starting);
    }

    return sortedResult;
  }
}

export function createDMap(
  goals: Point[],
  openCallback: (pos: Point, from: Point) => boolean,
  max = 50
) {
  const result: { [key: number]: number } = {};

  const queue: Array<{ position: Point; weight: number }> = [];
  for (const goal of goals) {
    result[goal.x * KEY_N + goal.y] = 0;
    queue.push({ position: goal, weight: 0 });
  }

  let next;
  while (queue.length) {
    const { position, weight } = queue.shift()!;
    if (weight < max) {
      allDirections.forEach((dir) => {
        next = getInDirection(position, dir);
        if (
          openCallback(next, position) &&
          result[next.x * KEY_N + next.y] == null
        ) {
          result[next.x * KEY_N + next.y] = weight + 1;
          queue.push({ position: next, weight: weight + 1 });
        }
      });
    }
  }

  return new DMap(result);
}
